import type { GoalsPredictionDetail } from '../../../types';

interface ProbabilitySummaryPanelProps {
  prediction: GoalsPredictionDetail;
}

export function ProbabilitySummaryPanel({ prediction }: ProbabilitySummaryPanelProps) {
  const formatProb = (prob: number | null | undefined) => {
    if (prob == null) return '--%';
    return `${(prob * 100).toFixed(1)}%`;
  };

  const ProbBar = ({ label, value }: { label: string; value: number | null | undefined }) => {
    const pct = (value || 0) * 100;
    return (
      <div className="goals-prob-bar-item">
        <div className="goals-prob-bar-header">
          <span className="goals-prob-bar-label">{label}</span>
          <span className="goals-prob-bar-value">{formatProb(value)}</span>
        </div>
        <div className="goals-prob-bar-track">
          <div
            className="goals-prob-bar-fill"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="goals-panel goals-probability-panel">
      {/* Expected Goals */}
      <div className="goals-panel-section">
        <h4>Expected Goals</h4>
        <div className="goals-xg-display">
          <div className="goals-xg-item">
            <span className="goals-xg-label">Home</span>
            <span className="goals-xg-value">{prediction.expected_goals.home?.toFixed(2) || '--'}</span>
          </div>
          <div className="goals-xg-item goals-xg-total">
            <span className="goals-xg-label">Total</span>
            <span className="goals-xg-value">{prediction.expected_goals.total?.toFixed(2) || '--'}</span>
          </div>
          <div className="goals-xg-item">
            <span className="goals-xg-label">Away</span>
            <span className="goals-xg-value">{prediction.expected_goals.away?.toFixed(2) || '--'}</span>
          </div>
        </div>
      </div>

      {/* BTTS */}
      <div className="goals-panel-section">
        <h4>Both Teams To Score</h4>
        <div className="goals-btts-display">
          <ProbBar label="BTTS Yes" value={prediction.btts.yes} />
          <ProbBar label="BTTS No" value={prediction.btts.no} />
        </div>
      </div>

      {/* Over/Under Lines */}
      <div className="goals-panel-section">
        <h4>Over/Under Goals</h4>
        <div className="goals-ou-grid">
          {['1.5', '2.5', '3.5', '4.5'].map((line) => (
            <div key={line} className="goals-ou-item">
              <span className="goals-ou-line">{line}</span>
              <div className="goals-ou-probs">
                <span className="goals-ou-over">O: {formatProb(prediction.over_under[line]?.over)}</span>
                <span className="goals-ou-under">U: {formatProb(prediction.over_under[line]?.under)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Combo Markets */}
      <div className="goals-panel-section">
        <h4>Combo Markets</h4>
        <div className="goals-combos-grid">
          <ProbBar label="BTTS + O2.5" value={prediction.combos.btts_o2_5} />
          <ProbBar label="Home Win + BTTS + O2.5" value={prediction.combos.home_win_btts_o2_5} />
          <ProbBar label="Away Win + BTTS + O2.5" value={prediction.combos.away_win_btts_o2_5} />
          <ProbBar label="Draw + BTTS" value={prediction.combos.draw_btts} />
        </div>
      </div>

      {/* Team to Score in Both Halves */}
      <div className="goals-panel-section">
        <h4>Team to Score in Both Halves</h4>
        <div className="goals-both-halves-grid">
          <ProbBar label="Home" value={prediction.team_score_both_halves.home} />
          <ProbBar label="Away" value={prediction.team_score_both_halves.away} />
        </div>
      </div>

      {/* Match Result */}
      <div className="goals-panel-section">
        <h4>Match Result (1X2)</h4>
        <div className="goals-1x2-display">
          <div className="goals-1x2-item">
            <span className="goals-1x2-label">Home</span>
            <span className="goals-1x2-value">{formatProb(prediction.match_result.home_win)}</span>
          </div>
          <div className="goals-1x2-item">
            <span className="goals-1x2-label">Draw</span>
            <span className="goals-1x2-value">{formatProb(prediction.match_result.draw)}</span>
          </div>
          <div className="goals-1x2-item">
            <span className="goals-1x2-label">Away</span>
            <span className="goals-1x2-value">{formatProb(prediction.match_result.away_win)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProbabilitySummaryPanel;
